// P14 — Mini-indicador de cota visível no header de /agentes/*.
//
// Botão clicável que mostra "X / Y hoje · X / Y mês" com cor que muda ao
// chegar perto do limite (>=70% amber, >=90% red). Clique abre detalhes.
//
// Atualização:
// - Polling 60s (fallback).
// - Custom DOM event `quota:refresh` — emitido por outras telas (ex.: depois
//   de enviar mensagem, depois de upload) pra refresh imediato sem WS.

import { FC, useCallback, useEffect, useState } from 'react';
import quotaService from '@/services/quota.service';
import { MyQuota } from '@/types/agents';
import { QUOTA_REFRESH_EVENT } from '@/utils/quotaRefreshBus';
import QuotaDetailsModal from './QuotaDetailsModal';

const POLL_MS = 60_000;

function fmtCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

const QuotaIndicator: FC = () => {
  const [quota, setQuota] = useState<MyQuota | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await quotaService.getMyQuota();
      setQuota(data);
    } catch {
      // Silencioso — se 401, o interceptor de auth cuida. Pra erros transitórios,
      // mantemos o último valor renderizado.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), POLL_MS);
    const handler = () => void refresh();
    window.addEventListener(QUOTA_REFRESH_EVENT, handler);
    return () => {
      clearInterval(id);
      window.removeEventListener(QUOTA_REFRESH_EVENT, handler);
    };
  }, [refresh]);

  if (!quota) return null;

  const dailyPct = quota.max_tokens_per_day
    ? (quota.tokens_used_today / quota.max_tokens_per_day) * 100
    : 0;
  const monthlyPct = quota.max_tokens_per_month
    ? (quota.tokens_used_this_month / quota.max_tokens_per_month) * 100
    : 0;
  const worstPct = Math.max(dailyPct, monthlyPct);

  const color =
    worstPct >= 90
      ? 'text-red-300 border-red-500/40 bg-red-500/10'
      : worstPct >= 70
        ? 'text-amber-200 border-amber-500/40 bg-amber-500/10'
        : 'text-content-tertiary border-surface-border bg-surface-secondary';

  return (
    <>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        data-testid="quota-indicator"
        title="Ver detalhes da sua cota de IA"
        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs hover:opacity-80 transition ${color}`}
      >
        <span className="material-symbols-outlined text-sm">data_usage</span>
        <span className="font-mono">
          {fmtCompact(quota.tokens_used_today)}/{fmtCompact(quota.max_tokens_per_day)} dia
        </span>
        <span aria-hidden className="opacity-50">
          ·
        </span>
        <span className="font-mono">
          {fmtCompact(quota.tokens_used_this_month)}/
          {fmtCompact(quota.max_tokens_per_month)} mês
        </span>
      </button>

      {modalOpen && (
        <QuotaDetailsModal
          quota={quota}
          onClose={() => setModalOpen(false)}
          onRefresh={refresh}
        />
      )}
    </>
  );
};

export default QuotaIndicator;
